import type { ConditionFieldRow } from "../../../../../api/types"

/**
 * `normalizeCas` and `conditionFieldValue` moved to `../../studio/conditionRows`
 * (L3, live test wave 5): `flowState.ts` (which may not depend on `studio-v2/`)
 * needs the same row lookup to stop the worklist's own "needs input" reading
 * from disagreeing with this mask's live one. Re-exported here so every
 * existing import site in this folder keeps working unchanged.
 */
export { conditionFieldValue, normalizeCas } from "../../studio/conditionRows"

/**
 * The identity a `ConditionFieldRow` is addressed by — the same 4-tuple the
 * backend keys the row on (`articleNumber`, `substanceCas`, `framework`,
 * `fieldKey`). Two rows with the same identity are the same cell; whichever
 * is merged in LAST wins. Exported so callers that need to key their OWN
 * maps by the same identity (e.g. an edits overlay) never duplicate the
 * tuple-join and risk drifting out of sync with the merge itself.
 */
export function rowIdentity(row: ConditionFieldRow): string {
  return `${row.articleNumber}|${row.substanceCas ?? "-"}|${row.framework ?? "-"}|${row.fieldKey}`
}

/**
 * `useSaveConditionFields` is a WHOLESALE REPLACE — its own doc comment: the
 * PUT payload IS the declaration's entire condition-fields table, not a
 * per-unit patch. A unit card that saved only the rows IT knows about would
 * silently delete every other unit's and every other Verordnung's condition
 * data on the very next keystroke; `concentration` rows in particular carry
 * `framework: null` and are SHARED across every Verordnung a substance
 * appears under, so even a same-framework neighbour statement is at risk.
 *
 * This merges one unit's edited rows into the declaration's existing row set
 * by identity, so a save always sends the complete table: every row this edit
 * does not touch passes through unchanged, and a row this edit DOES touch is
 * replaced (or added) with its new value.
 *
 * DELIBERATELY NEVER PRODUCES A RESERVED ROW. `__verified__` and
 * `__connected__` are step-3 signing marks (Screen C's `verifyStateToRows`);
 * B7's condition-field editor is a save surface, not a signing one, so a
 * caller passing only real field rows here cannot accidentally clear a sign-
 * off — the merge preserves whatever reserved rows already existed by simply
 * never targeting their identity.
 */
export function mergeConditionFieldRows(
  existing: readonly ConditionFieldRow[],
  edited: readonly ConditionFieldRow[],
): ConditionFieldRow[] {
  const byIdentity = new Map<string, ConditionFieldRow>()
  for (const row of existing) byIdentity.set(rowIdentity(row), row)
  for (const row of edited) byIdentity.set(rowIdentity(row), row)
  return [...byIdentity.values()]
}
